import React, { useEffect, useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/Avatar';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Separator } from '../components/Separator';
import { User, Mail, Phone, MapPin, Calendar, Shield, Building2, Award, Activity, KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getRoleAvatarUrl, getSession, type SessionRole } from '../lib/auth';
export function Profile() {
  const [session, setSession] = useState<SessionRole>(() => getSession());
  useEffect(() => {
    const onChange = (e: any) => {
      if (e.detail) setSession(e.detail as SessionRole);else setSession(getSession());
    };
    window.addEventListener('gs_session_change', onChange as EventListener);
    return () => window.removeEventListener('gs_session_change', onChange as EventListener);
  }, []);
  return <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6" data-id="element-2872">
      <PageHeader group="Sistem" title="Profil Pengguna" description="Detail profil, otoritas akses, dan aktivitas akun Anda dalam sistem." icon={User} data-id="element-2873" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-id="element-2874">
        {/* Identity Card */}
        <Card className="lg:col-span-4 bg-card border-border/50" data-id="element-2875">
          <CardContent className="p-6 flex flex-col items-center text-center" data-id="element-2876">
            <Avatar className="size-24 border-2 border-primary/30" data-id="element-2877">
              <AvatarImage src={getRoleAvatarUrl(session.roleId)} data-id="element-2878" />
              <AvatarFallback className="bg-primary/10 text-primary text-2xl font-bold" data-id="element-2879">
                {session.initials}
              </AvatarFallback>
            </Avatar>
            <h2 className="font-heading text-xl font-bold mt-4" data-id="element-2880">
              {session.role}
            </h2>
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-1" data-id="element-2881">
              {session.code} · Top Clearance
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-id="element-2882">
              <Badge className="bg-red-50 text-red-700 border-red-200 font-mono text-[10px]" data-id="element-2883">
                <Shield className="size-3 mr-1" data-id="element-2884" /> RAHASIA
              </Badge>
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-mono text-[10px]" data-id="element-2885">
                <Activity className="size-3 mr-1" data-id="element-2886" /> AKTIF
              </Badge>
            </div>
            <Separator className="my-5" data-id="element-2887" />
            <div className="w-full space-y-2.5 text-left" data-id="element-2888">
              <Row icon={Mail} label="Email" value={session.email} data-id="element-2889" />
              <Row icon={Phone} label="Telepon" value="+62 21 3811 1111" data-id="element-2890" />
              <Row icon={Building2} label="Instansi" value="Istana Negara" data-id="element-2891" />
              <Row icon={MapPin} label="Lokasi" value="Jakarta Pusat, ID" data-id="element-2892" />
              <Row icon={Calendar} label="Sesi Mulai" value={new Date().toLocaleString('id-ID')} data-id="element-2893" />
            </div>
            <Button asChild className="mt-5 w-full" variant="outline" data-id="element-2894">
              <Link to="/settings" data-id="element-2895">Edit Profil</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Right column */}
        <div className="lg:col-span-8 flex flex-col gap-4" data-id="element-2896">
          {/* Clearance & Roles */}
          <Card className="bg-card border-border/50" data-id="element-2897">
            <CardContent className="p-6" data-id="element-2898">
              <div className="flex items-center justify-between mb-4" data-id="element-2899">
                <div data-id="element-2900">
                  <h3 className="font-heading text-base font-bold flex items-center gap-2" data-id="element-2901">
                    <Award className="size-4 text-primary" data-id="element-2902" /> Otoritas &
                    Klasifikasi
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5" data-id="element-2903">
                    Tingkat akses dan peran fungsional dalam sistem GARUDA
                    SHIELD.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-2904">
                {[{
                label: 'Clearance',
                val: 'TOP SECRET'
              }, {
                label: 'Peran Utama',
                val: session.role
              }, {
                label: 'Skema RBAC',
                val: session.code
              }, {
                label: 'Region Akses',
                val: 'NASIONAL'
              }].map(s => <div key={s.label} className="p-3 rounded-lg border border-border/50 bg-muted/20" data-id="element-2905">
                    <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-2906">
                      {s.label}
                    </p>
                    <p className="font-heading text-sm font-bold mt-1" data-id="element-2907">
                      {s.val}
                    </p>
                  </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Permission Matrix */}
          <Card className="bg-card border-border/50" data-id="element-2908">
            <CardContent className="p-6" data-id="element-2909">
              <h3 className="font-heading text-base font-bold mb-4" data-id="element-2910">
                Matriks Hak Akses
              </h3>
              <div className="space-y-2" data-id="element-2911">
                {[{
                mod: 'Executive Dashboard',
                view: true,
                edit: true,
                export: true
              }, {
                mod: 'Threat Intelligence',
                view: true,
                edit: true,
                export: true
              }, {
                mod: 'Actor Network',
                view: true,
                edit: true,
                export: true
              }, {
                mod: 'Command Tasking',
                view: true,
                edit: true,
                export: true
              }, {
                mod: 'Legal Evidence',
                view: true,
                edit: false,
                export: true
              }, {
                mod: 'Audit Log',
                view: true,
                edit: false,
                export: false
              }].map(r => <div key={r.mod} className="grid grid-cols-12 items-center gap-2 p-2.5 rounded-lg border border-border/50 bg-muted/20" data-id="element-2912">
                    <span className="col-span-6 sm:col-span-5 text-sm" data-id="element-2913">
                      {r.mod}
                    </span>
                    <div className="col-span-2 text-center" data-id="element-2914">
                      <Perm v={r.view} label="View" data-id="element-2915" />
                    </div>
                    <div className="col-span-2 text-center" data-id="element-2916">
                      <Perm v={r.edit} label="Edit" data-id="element-2917" />
                    </div>
                    <div className="col-span-2 text-center" data-id="element-2918">
                      <Perm v={r.export} label="Export" data-id="element-2919" />
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="bg-card border-border/50" data-id="element-2920">
            <CardContent className="p-6" data-id="element-2921">
              <div className="flex items-center justify-between mb-4" data-id="element-2922">
                <h3 className="font-heading text-base font-bold flex items-center gap-2" data-id="element-2923">
                  <KeyRound className="size-4 text-primary" data-id="element-2924" /> Aktivitas Sesi
                  Terakhir
                </h3>
                <Badge variant="outline" className="font-mono text-[10px]" data-id="element-2925">
                  7 hari terakhir
                </Badge>
              </div>
              <div className="space-y-2" data-id="element-2926">
                {[{
                t: '14:22 hari ini',
                act: 'Login berhasil dari Istana Negara',
                ip: '10.0.1.4'
              }, {
                t: '08:15 hari ini',
                act: 'Membuka Daily Brief',
                ip: '10.0.1.4'
              }, {
                t: 'Kemarin 18:40',
                act: 'Mengubah peran simulasi',
                ip: '10.0.1.4'
              }, {
                t: 'Kemarin 09:02',
                act: 'Export laporan Threat Intel',
                ip: '10.0.1.4'
              }, {
                t: '2 hari lalu',
                act: 'Login dari mobile (iOS)',
                ip: '202.78.x.x'
              }].map((a, i) => <div key={i} className="flex items-center justify-between text-xs p-2.5 rounded-lg border border-border/50 bg-muted/20" data-id="element-2927">
                    <div data-id="element-2928">
                      <p className="font-medium" data-id="element-2929">{a.act}</p>
                      <p className="font-mono text-[10px] text-muted-foreground" data-id="element-2930">
                        IP: {a.ip}
                      </p>
                    </div>
                    <span className="font-mono text-[10px] text-muted-foreground" data-id="element-2931">
                      {a.t}
                    </span>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}
function Row({
  icon: Icon,
  label,
  value
}: {
  icon: any;
  label: string;
  value: string;
}) {
  return <div className="flex items-center gap-2.5 text-xs" data-id="element-2932">
      <Icon className="size-3.5 text-muted-foreground shrink-0" data-id="element-2933" />
      <span className="text-muted-foreground w-16 shrink-0" data-id="element-2934">{label}</span>
      <span className="font-medium truncate" data-id="element-2935">{value}</span>
    </div>;
}
function Perm({
  v,
  label
}: {
  v: boolean;
  label: string;
}) {
  return <Badge variant="outline" className={`font-mono text-[9px] ${v ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-muted text-muted-foreground border-border'}`} data-id="element-2936">
      {v ? '✓' : '—'} {label}
    </Badge>;
}